/**
 * Per-business-type Opening Plan profiles.
 *
 * Single source of truth used by `generateMockResults` (in `businessData.ts`)
 * and by UI surfaces (Dashboard "Setup Details", Permits, Budget, etc.) to
 * tailor each Opening Plan to the realities of a given concept.
 *
 * The numbers come from the customization brief and are intentionally
 * conservative US-baseline ranges. City multipliers in `businessData.ts`
 * scale `startupUSD` by location so the number a Madrid bakery sees is
 * different from a New York bakery.
 *
 * Adding a new type? Add an entry below and update the `businessTypeOptions`
 * array in `src/pages/Onboarding.tsx`.
 */

export interface BusinessTypeProfile {
  /** Internal id, must match the option id in Onboarding's businessTypeOptions */
  id: string;
  /** Human-readable label shown in summaries */
  label: string;
  /** US-baseline all-in startup cost range, before city/size multipliers. */
  startupUSD: { low: number; high: number };
  /** Total headcount range at steady-state (lean → full team). */
  staffRange: { min: number; max: number };
  /** Number of permits/licenses typically required. */
  permitsRange: { min: number; max: number };
  /** Realistic time-to-open range, in months. */
  timelineMonths: { min: number; max: number };
  /** Typical operating footprint (square feet). */
  sqftRange: { min: number; max: number };
  /** Short investor-facing tagline shown in the Setup card. */
  tagline: string;
}

export const BUSINESS_TYPE_PROFILES: Record<string, BusinessTypeProfile> = {
  "restaurant": {
    id: "restaurant",
    label: "Restaurant",
    startupUSD: { low: 150_000, high: 500_000 },
    staffRange: { min: 15, max: 25 },
    permitsRange: { min: 8, max: 12 },
    timelineMonths: { min: 6, max: 12 },
    sqftRange: { min: 1_200, max: 3_000 },
    tagline: "Full-service concept — highest setup, highest revenue ceiling.",
  },
  "coffee-shop": {
    id: "coffee-shop",
    label: "Café",
    startupUSD: { low: 50_000, high: 150_000 },
    staffRange: { min: 4, max: 8 },
    permitsRange: { min: 5, max: 7 },
    timelineMonths: { min: 3, max: 6 },
    sqftRange: { min: 600, max: 1_400 },
    tagline: "Lean footprint, high traffic, fast payback.",
  },
  "bar": {
    id: "bar",
    label: "Bar",
    startupUSD: { low: 100_000, high: 300_000 },
    staffRange: { min: 6, max: 12 },
    permitsRange: { min: 6, max: 10 },
    timelineMonths: { min: 4, max: 8 },
    sqftRange: { min: 900, max: 2_200 },
    tagline: "Liquor-license driven — long lead time, strong margins.",
  },
  "bakery": {
    id: "bakery",
    label: "Bakery",
    startupUSD: { low: 40_000, high: 120_000 },
    staffRange: { min: 3, max: 6 },
    permitsRange: { min: 4, max: 6 },
    timelineMonths: { min: 2, max: 4 },
    sqftRange: { min: 600, max: 1_400 },
    tagline: "Production-led concept — early starts, loyal regulars.",
  },
  "food-truck": {
    id: "food-truck",
    label: "Food Truck",
    startupUSD: { low: 20_000, high: 80_000 },
    staffRange: { min: 1, max: 3 },
    permitsRange: { min: 4, max: 6 },
    timelineMonths: { min: 1, max: 3 },
    sqftRange: { min: 80, max: 220 },
    tagline: "Lowest barrier to entry — mobility is the moat.",
  },
  "ghost-kitchen": {
    id: "ghost-kitchen",
    label: "Virtual Kitchen",
    startupUSD: { low: 15_000, high: 60_000 },
    staffRange: { min: 2, max: 5 },
    permitsRange: { min: 3, max: 5 },
    timelineMonths: { min: 1, max: 2 },
    sqftRange: { min: 200, max: 600 },
    tagline: "Delivery-only — no front-of-house, fastest launch.",
  },
  "pizzeria": {
    id: "pizzeria",
    label: "Pizzeria",
    startupUSD: { low: 80_000, high: 200_000 },
    staffRange: { min: 6, max: 12 },
    permitsRange: { min: 6, max: 8 },
    timelineMonths: { min: 3, max: 6 },
    sqftRange: { min: 800, max: 1_800 },
    tagline: "Focused menu, equipment-heavy, high throughput.",
  },
  "juice-bar": {
    id: "juice-bar",
    label: "Juice Bar",
    startupUSD: { low: 30_000, high: 80_000 },
    staffRange: { min: 2, max: 4 },
    permitsRange: { min: 3, max: 5 },
    timelineMonths: { min: 2, max: 3 },
    sqftRange: { min: 400, max: 900 },
    tagline: "Health-led grab-and-go — fast prep, premium pricing.",
  },
  "hotel": {
    id: "hotel",
    label: "Hotel & Lodging",
    startupUSD: { low: 500_000, high: 5_000_000 },
    staffRange: { min: 20, max: 50 },
    permitsRange: { min: 10, max: 15 },
    timelineMonths: { min: 12, max: 24 },
    sqftRange: { min: 5_000, max: 50_000 },
    tagline: "Capital-intensive — multi-department operation.",
  },
  "bed-and-breakfast": {
    id: "bed-and-breakfast",
    label: "Bed & Breakfast",
    startupUSD: { low: 100_000, high: 400_000 },
    staffRange: { min: 3, max: 8 },
    permitsRange: { min: 6, max: 10 },
    timelineMonths: { min: 6, max: 12 },
    sqftRange: { min: 2_000, max: 6_000 },
    tagline: "Owner-operated lodging — hospitality + light F&B.",
  },
  "private-events": {
    id: "private-events",
    label: "Private Events & Catering",
    startupUSD: { low: 30_000, high: 100_000 },
    staffRange: { min: 3, max: 10 },
    permitsRange: { min: 4, max: 7 },
    timelineMonths: { min: 2, max: 4 },
    sqftRange: { min: 600, max: 2_000 },
    tagline: "Event-driven — variable crew, premium per-cover pricing.",
  },
  "private-chef": {
    id: "private-chef",
    label: "Private Chef",
    startupUSD: { low: 10_000, high: 40_000 },
    staffRange: { min: 1, max: 2 },
    permitsRange: { min: 2, max: 4 },
    timelineMonths: { min: 1, max: 2 },
    sqftRange: { min: 0, max: 200 },
    tagline: "Solo operator — minimal overhead, premium relationships.",
  },
};

export function getBusinessTypeProfile(
  businessTypeId: string,
): BusinessTypeProfile | undefined {
  return BUSINESS_TYPE_PROFILES[businessTypeId];
}

/** Format a startup range as `$150K–$500K`. */
export function formatStartupRange(p: BusinessTypeProfile): string {
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
      : `$${Math.round(n / 1_000)}K`;
  return `${fmt(p.startupUSD.low)}–${fmt(p.startupUSD.high)}`;
}

/** Format a `min–max` range with optional unit suffix. */
export function formatRange(
  range: { min: number; max: number },
  unit?: string,
): string {
  const u = unit ? ` ${unit}` : "";
  return range.min === range.max
    ? `${range.min}${u}`
    : `${range.min}–${range.max}${u}`;
}